import { useState } from 'react'
import {
  useDeleteServiceChecklist,
  useDeviceTypes,
  useSaveServiceChecklist,
  useServiceChecklists
} from '../../api/queries'
import type { ServiceChecklist, ServiceItemType } from '../../api/types'
import { Card, EmptyState, ErrorBox, Loading } from '../../components/ui'

/**
 * Checklisteskabeloner til servicebesøg (kun administratorer).
 *
 * Et punkt er enten en afkrydsning (i orden / afvigelse) eller en måling med en
 * enhed og et forventet interval. Ligger målingen uden for intervallet, markeres
 * punktet automatisk som en afvigelse - montøren skal ikke huske grænserne
 * (docs/DECISIONS.md beslutning 026).
 */
export function ServiceChecklistsPage() {
  const { data, isLoading, error } = useServiceChecklists(null, true)
  const [editing, setEditing] = useState<ServiceChecklist | null>(null)
  const [creating, setCreating] = useState(false)
  /** Liste der skal bruges som udgangspunkt for en ny - punkterne kopieres med. */
  const [copying, setCopying] = useState<ServiceChecklist | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  if (creating || editing || copying) {
    return (
      <ChecklistForm
        checklist={editing}
        copyFrom={copying}
        onClose={() => {
          setCreating(false)
          setEditing(null)
          setCopying(null)
        }}
      />
    )
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <p className="small muted" style={{ margin: 0, flex: 1 }}>
          Listerne foreslås ud fra enhedens type, når et servicebesøg startes. Punkterne kopieres
          ind i besøget, så en gammel rapport altid viser, hvad der faktisk blev tjekket.
        </p>
        <button className="btn btn--primary" onClick={() => setCreating(true)}>
          Ny checkliste
        </button>
      </div>

      {data && data.length === 0 && (
        <EmptyState>
          Der er ingen servicechecklister endnu. Uden en liste kan et servicebesøg stadig
          gennemføres, men så er der ikke noget at gennemgå.
        </EmptyState>
      )}

      <div className="stack" style={{ gap: 12 }}>
        {data?.map((checklist) => (
          <Card
            key={checklist.id}
            title={checklist.name}
            actions={
              <div className="row">
                {/* Det hurtigste udgangspunkt for en ny liste er som regel en
                    der ligner. Kopien aabnes med det samme, saa den kan rettes
                    til foer den gemmes. */}
                <button className="btn btn--sm" onClick={() => setCopying(checklist)}>
                  Kopiér
                </button>
                <button className="btn btn--sm" onClick={() => setEditing(checklist)}>
                  Rediger
                </button>
              </div>
            }
          >
            <div className="tiny muted" style={{ marginBottom: 8 }}>
              {checklist.deviceType ? `Til ${checklist.deviceType}` : 'Passer til alle enhedstyper'} ·{' '}
              {checklist.items.length} punkter
              {!checklist.isActive && ' · ikke aktiv'}
            </div>

            {checklist.description && <p className="small">{checklist.description}</p>}

            <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {checklist.items.map((item) => (
                <li key={item.id}>
                  {item.text}
                  {item.itemType === 'measurement' && (
                    <span className="muted">
                      {' '}
                      — måling i {item.unit}
                      {item.minValue !== null || item.maxValue !== null
                        ? ` (forventet ${item.minValue ?? ''}-${item.maxValue ?? ''})`
                        : ''}
                    </span>
                  )}
                  {!item.isRequired && <span className="tiny muted"> (valgfri)</span>}
                </li>
              ))}
            </ol>
          </Card>
        ))}
      </div>
    </>
  )
}

interface ItemDraft {
  text: string
  itemType: ServiceItemType
  unit: string
  minValue: string
  maxValue: string
  isRequired: boolean
  helpText: string
}

function emptyItem(): ItemDraft {
  return { text: '', itemType: 'check', unit: '', minValue: '', maxValue: '', isRequired: true, helpText: '' }
}

function ChecklistForm({
  checklist,
  copyFrom,
  onClose
}: {
  checklist: ServiceChecklist | null
  /** Liste der kopieres fra. Punkterne tages med, men listen gemmes som en ny. */
  copyFrom?: ServiceChecklist | null
  onClose: () => void
}) {
  const save = useSaveServiceChecklist(checklist?.id)
  const remove = useDeleteServiceChecklist()

  // Ved kopiering redigeres ikke originalen - den bruges kun som udgangspunkt.
  const source = checklist ?? copyFrom ?? null

  const [form, setForm] = useState({
    // Navnet skal ændres, men kopien skal kunne gemmes uden at man støder på
    // "findes allerede" med det samme.
    name: checklist ? checklist.name : copyFrom ? `${copyFrom.name} (kopi)` : '',
    description: source?.description ?? '',
    deviceType: source?.deviceType ?? '',
    isActive: source?.isActive ?? true,
    sortOrder: source?.sortOrder ?? 0
  })

  const [items, setItems] = useState<ItemDraft[]>(
    source?.items.length
      ? source.items.map((item) => ({
          text: item.text,
          itemType: item.itemType,
          unit: item.unit ?? '',
          minValue: item.minValue !== null && item.minValue !== undefined ? String(item.minValue) : '',
          maxValue: item.maxValue !== null && item.maxValue !== undefined ? String(item.maxValue) : '',
          isRequired: item.isRequired,
          helpText: item.helpText ?? ''
        }))
      : [emptyItem()]
  )

  const [error, setError] = useState<string | null>(null)

  function updateItem(index: number, patch: Partial<ItemDraft>) {
    setItems((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)))
  }

  function move(index: number, delta: number) {
    const target = index + delta
    if (target < 0 || target >= items.length) return

    setItems((current) => {
      const next = [...current]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    try {
      await save.mutateAsync({
        ...form,
        deviceType: form.deviceType || null,
        description: form.description || null,
        items: items.map((item) => ({
          text: item.text,
          itemType: item.itemType,
          unit: item.itemType === 'measurement' ? item.unit : null,
          minValue: item.itemType === 'measurement' && item.minValue !== '' ? Number(item.minValue) : null,
          maxValue: item.itemType === 'measurement' && item.maxValue !== '' ? Number(item.maxValue) : null,
          isRequired: item.isRequired,
          helpText: item.helpText || null
        }))
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checklisten kunne ikke gemmes.')
    }
  }

  return (
    <Card
      title={
        checklist
          ? `Ret ${checklist.name}`
          : copyFrom
            ? `Ny checkliste ud fra "${copyFrom.name}"`
            : 'Ny servicecheckliste'
      }
    >
      <form onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Navn *</span>
            <input
              required
              placeholder="f.eks. Årligt eftersyn, nødbelysningsbatteri"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>

          <label className="field">
            <span className="field__label">Enhedstype</span>
            <ChecklistTypeSelect
              value={form.deviceType}
              onChange={(deviceType) => setForm({ ...form, deviceType })}
            />
            <span className="tiny muted">Listen foreslås på enheder af denne type. Nye typer tilføjes under Enhedstyper.</span>
          </label>
        </div>

        <label className="field">
          <span className="field__label">Beskrivelse</span>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </label>

        <div className="row row--between" style={{ marginTop: 8, marginBottom: 8 }}>
          <strong className="small">Punkter</strong>
          <button className="btn btn--sm" type="button" onClick={() => setItems([...items, emptyItem()])}>
            Tilføj punkt
          </button>
        </div>

        <div className="stack" style={{ gap: 10 }}>
          {items.map((item, index) => (
            <div key={index} className="checklist-item">
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <input
                  required
                  style={{ flex: 1, minWidth: 180 }}
                  placeholder="Hvad skal tjekkes?"
                  value={item.text}
                  onChange={(e) => updateItem(index, { text: e.target.value })}
                />
                <select
                  style={{ maxWidth: 150 }}
                  value={item.itemType}
                  onChange={(e) => updateItem(index, { itemType: e.target.value as ServiceItemType })}
                >
                  <option value="check">I orden / afvigelse</option>
                  <option value="measurement">Måling</option>
                </select>
                <button className="btn btn--sm" type="button" onClick={() => move(index, -1)} title="Flyt op">
                  ↑
                </button>
                <button className="btn btn--sm" type="button" onClick={() => move(index, 1)} title="Flyt ned">
                  ↓
                </button>
                <button
                  className="btn btn--sm"
                  type="button"
                  disabled={items.length === 1}
                  onClick={() => setItems(items.filter((_, i) => i !== index))}
                >
                  Fjern
                </button>
              </div>

              {item.itemType === 'measurement' && (
                <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  <input
                    required
                    style={{ maxWidth: 90 }}
                    placeholder="Enhed"
                    value={item.unit}
                    onChange={(e) => updateItem(index, { unit: e.target.value })}
                  />
                  <input
                    type="number"
                    step="any"
                    style={{ maxWidth: 110 }}
                    placeholder="Mindst"
                    value={item.minValue}
                    onChange={(e) => updateItem(index, { minValue: e.target.value })}
                  />
                  <input
                    type="number"
                    step="any"
                    style={{ maxWidth: 110 }}
                    placeholder="Højst"
                    value={item.maxValue}
                    onChange={(e) => updateItem(index, { maxValue: e.target.value })}
                  />
                  <span className="tiny muted">
                    Uden for intervallet markeres punktet automatisk som afvigelse
                  </span>
                </div>
              )}

              <div className="row" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <input
                  style={{ flex: 1, minWidth: 180 }}
                  placeholder="Hjælpetekst (valgfri), f.eks. hvor målingen tages"
                  value={item.helpText}
                  onChange={(e) => updateItem(index, { helpText: e.target.value })}
                />
                <label className="row tiny muted" style={{ gap: 4 }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={item.isRequired}
                    onChange={(e) => updateItem(index, { isRequired: e.target.checked })}
                  />
                  Påkrævet
                </label>
              </div>
            </div>
          ))}
        </div>

        <label className="row small" style={{ gap: 6, marginTop: 12 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={!form.isActive}
            onChange={(e) => setForm({ ...form, isActive: !e.target.checked })}
          />
          Listen er ikke aktiv (foreslås ikke ved nye besøg)
        </label>

        {error && <div className="error-box" style={{ marginTop: 12 }}>{error}</div>}

        <div className="btn-group" style={{ marginTop: 12 }}>
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
          {checklist && (
            <button
              className="btn"
              type="button"
              style={{ marginLeft: 'auto' }}
              disabled={remove.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Slet "${checklist.name}"? Tidligere servicerapporter beholder deres egen kopi af punkterne.`
                  )
                ) {
                  remove.mutate(checklist.id, { onSuccess: onClose })
                }
              }}
            >
              Slet
            </button>
          )}
        </div>
      </form>
    </Card>
  )
}

/**
 * Typen vælges fra den faste liste (beslutning 105), så den rammer enhedernes type præcist.
 * En type fra før listen står der stadig og kan blive stående.
 */
function ChecklistTypeSelect({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { data: types } = useDeviceTypes()
  const known = (types ?? []).some((t) => t.name.toLowerCase() === value.trim().toLowerCase())

  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Alle typer</option>
      {(types ?? []).map((t) => (
        <option key={t.id} value={t.name}>
          {t.name}
        </option>
      ))}
      {value && !known && <option value={value}>{value} (ikke på listen)</option>}
    </select>
  )
}
